// src/components/ContentActionMenu.js
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ContentActionMenu.css";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";

import {
  createModerationReportData,
  blockUserData,
} from "../services/moderationDataService";

/**
 * Zentrales Drei-Punkte-Menü für:
 * - Teilen
 * - Melden
 * - Nutzer blockieren
 *
 * Wichtig:
 * - Menü wird per Portal direkt in document.body gerendert.
 * - Dadurch verschwindet es nicht mehr hinter Beiträgen/Postboxen.
 * - Bestätigungs-Popups werden ebenfalls per Portal gerendert.
 * - Scroll-Lock verhindert iOS/Capacitor-Sprünge beim geöffneten Menü.
 */
function ContentActionMenu({
  contentType = "content",
  contentId = null,
  parentId = null,

  ownerId = null,
  ownerName = "",

  contentTitle = "",
  contentText = "",

  currentUser = null,

  shareLabel = "Beitrag teilen",
  reportLabel = "Beitrag melden",
  blockLabel = "Nutzer blockieren",

  shareTitle = "Edens Ernte",
  shareText = "",
  shareUrl = "",

  showShare = true,
  showReport = true,
  showBlock = true,

  onLoginRequired = null,
  onUserBlocked = null,
  onBlockedUser = null,
  onActionToast = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showReportConfirm, setShowReportConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [reportReason, setReportReason] = useState("");

  const wrapperRef = useRef(null);

  const userId = currentUser?.uid || null;
  const userEmail = currentUser?.email || "";
  const WEB_BASE_URL = "https://edens-ernte.app";

  const closeMenu = () => {
    setIsOpen(false);
  };

  const showToast = (text) => {
    if (typeof onActionToast === "function") {
      onActionToast(text);
    }
  };

  const requireLogin = () => {
    closeMenu();

    if (typeof onLoginRequired === "function") {
      onLoginRequired();
    } else {
      showToast("Login erforderlich");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current) return;

      const target = event.target;

      if (
        target.closest?.(".content-action-menu-sheet") ||
        target.closest?.(".content-action-menu-backdrop") ||
        target.closest?.(".content-action-confirm-overlay")
      ) {
        return;
      }

      if (!wrapperRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setShowReportConfirm(false);
        setShowBlockConfirm(false);
        setReportReason("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Scroll-Lock: wichtig für iOS / Capacitor, damit das Menü nicht springt.
  useEffect(() => {
    if (!isOpen && !showReportConfirm && !showBlockConfirm) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;

    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;
    const previousBodyWidth = document.body.style.width;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      document.body.style.width = previousBodyWidth;
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;

      window.scrollTo(0, scrollY);
    };
  }, [isOpen, showReportConfirm, showBlockConfirm]);

  const handleToggleMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const buildPublicShareUrl = () => {
    if (shareUrl && shareUrl.startsWith("https://")) {
      return shareUrl;
    }

    if (shareUrl && shareUrl.startsWith("/")) {
      return `${WEB_BASE_URL}${shareUrl}`;
    }

    if (typeof window !== "undefined") {
      const pathname = window.location?.pathname || "";

      if (pathname && pathname !== "/") {
        return `${WEB_BASE_URL}${pathname}`;
      }
    }

    return WEB_BASE_URL;
  };

  const handleShare = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const finalShareUrl = buildPublicShareUrl();
    const finalTitle = shareTitle || "Edens Ernte";
    const finalText = shareText || finalTitle;

    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: finalTitle,
          text: finalText,
          url: finalShareUrl,
          dialogTitle: finalTitle,
        });

        closeMenu();
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: finalTitle,
          text: finalText,
          url: finalShareUrl,
        });

        closeMenu();
        return;
      }

      if (navigator.clipboard && finalShareUrl) {
        await navigator.clipboard.writeText(finalShareUrl);
        showToast("Link kopiert");
        closeMenu();
        return;
      }

      showToast("Teilen nicht möglich");
      closeMenu();
    } catch (error) {
      closeMenu();

      if (error?.name !== "AbortError") {
        console.warn("Teilen fehlgeschlagen:", error);
        showToast("Teilen nicht möglich");
      }
    }
  };

  const handleAskReport = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!userId) {
      requireLogin();
      return;
    }

    closeMenu();
    setReportReason("");
    setShowReportConfirm(true);
  };

  const handleAskBlock = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!userId) {
      requireLogin();
      return;
    }

    if (!ownerId) {
      closeMenu();
      showToast("Nutzer konnte nicht ermittelt werden");
      return;
    }

    if (String(ownerId) === String(userId)) {
      closeMenu();
      showToast("Du kannst dich nicht selbst blockieren");
      return;
    }

    closeMenu();
    setShowBlockConfirm(true);
  };

  const handleConfirmReport = async () => {
    if (isWorking) return;

    if (!userId) {
      setShowReportConfirm(false);
      requireLogin();
      return;
    }

    const cleanReason = reportReason.trim();

    if (!cleanReason) {
      showToast("Bitte gib einen Grund für die Meldung an.");
      return;
    }

    setIsWorking(true);

    try {
      await createModerationReportData({
  contentType,
  contentId: contentId || null,
  parentId: parentId || null,

  ownerId: ownerId || null,
  ownerName: ownerName || "",

  reporterId: userId,
  reporterEmail: userEmail || "",

  reason: cleanReason,
  details: cleanReason,

  contentSnapshot: {
    title: contentTitle || shareTitle || "",
    text: contentText || shareText || "",
    url: buildPublicShareUrl(),
  },

  extra: {
    reportReason: cleanReason,
    shareTitle: shareTitle || "",
    shareUrl: buildPublicShareUrl(),
    contentTitle: contentTitle || "",
    contentText: contentText || "",
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "",
    platform:
      typeof Capacitor?.getPlatform === "function"
        ? Capacitor.getPlatform()
        : "",
  },
});

      setReportReason("");
      setShowReportConfirm(false);
      showToast("Meldung wurde gespeichert");
    } catch (error) {
      console.warn("Meldung konnte nicht gespeichert werden:", error);
      showToast("Meldung konnte nicht gespeichert werden");
    } finally {
      setIsWorking(false);
    }
  };

  const handleConfirmBlock = async () => {
    if (isWorking) return;

    if (!userId) {
      setShowBlockConfirm(false);
      requireLogin();
      return;
    }

    if (!ownerId || String(ownerId) === String(userId)) {
      setShowBlockConfirm(false);
      showToast("Nutzer kann nicht blockiert werden");
      return;
    }

    setIsWorking(true);

    try {
      await blockUserData({
        myUid: userId,
        blockedUserId: ownerId,
        blockedUserName: ownerName || "",

        sourceContentType: contentType || "",
        sourceContentId: contentId || null,
      });

      if (typeof onBlockedUser === "function") {
        onBlockedUser(ownerId);
      }

      if (typeof onUserBlocked === "function") {
        onUserBlocked(ownerId);
      }

      setShowBlockConfirm(false);
      showToast("Nutzer wurde blockiert");
    } catch (error) {
      console.warn("Blockierung konnte nicht gespeichert werden:", error);
      showToast("Nutzer konnte nicht blockiert werden");
    } finally {
      setIsWorking(false);
    }
  };

  const menuLayer =
    isOpen &&
    createPortal(
      <>
        <div
          className="content-action-menu-backdrop"
          onClick={(event) => {
            event.stopPropagation();
            closeMenu();
          }}
          onTouchMove={(event) => {
            event.preventDefault();
          }}
        />

        <div
          className="content-action-menu-sheet"
          onClick={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          {showShare && (
            <button type="button" onClick={handleShare}>
              {shareLabel}
            </button>
          )}

          {showReport && (
            <button type="button" onClick={handleAskReport}>
              {reportLabel}
            </button>
          )}

          {showBlock && (
            <button type="button" className="danger" onClick={handleAskBlock}>
              {blockLabel}
            </button>
          )}

          <button type="button" className="cancel" onClick={closeMenu}>
            Abbrechen
          </button>
        </div>
      </>,
      document.body
    );

  const reportConfirmLayer =
    showReportConfirm &&
    createPortal(
      <div
        className="modal-overlay content-action-confirm-overlay"
        onClick={(event) => {
          event.stopPropagation();
          if (!isWorking) {
            setReportReason("");
            setShowReportConfirm(false);
          }
        }}
      >
        <div
          className="modal-content"
          onClick={(event) => event.stopPropagation()}
        >
          <h3>{reportLabel}?</h3>

          <p>
            Möchtest du diesen Inhalt wirklich melden? Die Meldung wird von
            Edens Ernte geprüft.
          </p>

          <label className="report-reason-label">
            Was ist der Grund der Meldung?
          </label>

          <textarea
            className="report-reason-textarea"
            value={reportReason}
            onChange={(event) => setReportReason(event.target.value)}
            placeholder="Beschreibe kurz, warum du diesen Inhalt melden möchtest..."
            rows="4"
            maxLength={500}
            disabled={isWorking}
          />

          <div className="report-reason-counter">
            {reportReason.length}/500
          </div>

          <div className="modal-buttons">
            <button
              className="delete-btn"
              onClick={handleConfirmReport}
              disabled={isWorking || !reportReason.trim()}
            >
              {isWorking ? "Wird gespeichert…" : "Melden"}
            </button>

            <button
              className="cancel-btn"
              onClick={() => {
                setReportReason("");
                setShowReportConfirm(false);
              }}
              disabled={isWorking}
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  const blockConfirmLayer =
    showBlockConfirm &&
    createPortal(
      <div
        className="modal-overlay content-action-confirm-overlay"
        onClick={(event) => {
          event.stopPropagation();
          if (!isWorking) setShowBlockConfirm(false);
        }}
      >
        <div
          className="modal-content"
          onClick={(event) => event.stopPropagation()}
        >
          <h3>{blockLabel}?</h3>
          <p>
            Du siehst künftig möglichst keine Inhalte und Nachrichten dieses
            Nutzers mehr. Du kannst die Blockierung später in deinem Profil
            wieder aufheben.
          </p>

          <div className="modal-buttons">
            <button
              className="delete-btn"
              onClick={handleConfirmBlock}
              disabled={isWorking}
            >
              {isWorking ? "Wird gespeichert…" : "Blockieren"}
            </button>

            <button
              className="cancel-btn"
              onClick={() => setShowBlockConfirm(false)}
              disabled={isWorking}
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div
      className="content-action-menu-wrapper"
      ref={wrapperRef}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="content-action-menu-trigger"
        title="Weitere Optionen"
        aria-label="Weitere Optionen"
        onClick={handleToggleMenu}
      >
        ⋮
      </button>

      {menuLayer}
      {reportConfirmLayer}
      {blockConfirmLayer}
    </div>
  );
}

export default ContentActionMenu;